<template>
  <a-board :over="over" />
</template>

<script setup>
import { fabric } from 'fabric'
import { onMounted } from 'vue'
import { useUserStore, useTransformStore, useSnapshotStore, useSessionStore } from '@/stores'
import { useAppMachine } from '@/state'
import { Dart } from '@/libraries/fabric'
import { board as messages } from '@/messages'
import { capitalizeFirst } from '@/utils'
import { messenger } from '@communication'
import { procedures } from '@communication'
import { useAppStore } from '@/stores'

defineProps({ over: Boolean })

const user = useUserStore()
const snapshot = useSnapshotStore()
const session = useSessionStore()
const transform = useTransformStore()
const app = useAppStore()
const boardProcedure = procedures.get('boardProcedure')
const snapshotProcedure = procedures.get('snapshotProcedure')
boardProcedure.addListener(procedureListener)
snapshotProcedure.addListener(snapshotListener)

const { service } = useAppMachine()

const dart = new Dart()
let canvas = null

const state = {
  listening: false
}

// STATE HANDLERS

service.subscribe((state) => {
  if (canvas) {
    let { meeting } = state.value.app.view
    if (typeof meeting === 'object') {
      meeting = Object.keys(meeting).map((key) => `${key}.${meeting[key]}`).join('')
    }
    switch (meeting) {
      case 'call': {
        canvas.clear()
        transform.setTransform({
          zoom: 1,
          x: 0,
          y: 0
        })
        break
      }
      case 'draw.default': {
        canvas.backgroundColor = 'black'
        break
      }
    }
  }
})

// STORE HANDLERS

transform.$onAction(({ name, args, after }) => {
  let viewportTransform = [1, 0, 0, 1, 0, 0]
  switch (name) {
    case 'setTransform': {
      const [{ zoom, x, y }] = args
      viewportTransform = [
        zoom,
        0,
        0,
        zoom,
        x,
        y
      ]
      dart.updateScale(zoom)
      break
    }
    case 'setTranslation': {
      const [{ x, y }] = args
      const { zoom } = transform
      viewportTransform = [
        zoom,
        0,
        0,
        zoom,
        x,
        y
      ]
      break
    }
  }
  after(() => {
    canvas.viewportTransform = viewportTransform
    canvas.renderAll()
  })
})

user.$onAction(({ name, args }) => {
  switch (name) {
    case 'setViewport': {
      const [width, height] = args
      canvas.setDimensions({
        width,
        height
      })
      break
    }
  }
})

// CANVAS HANDLERS

function setCanvasEventHandlers() {
  canvas.on('mouse:down', (event) => {
    const { x: left, y: top } = canvas.getPointer(event.e)
    state.listening = true
    boardProcedure.send(messages.tapCastedMessage({
      action: 'start',
      left,
      top
    }))
  }).on('mouse:move', (event) => {
    if (state.listening) {
      const { x: left, y: top } = canvas.getPointer(event.e)
      boardProcedure.send(messages.tapCastedMessage({
        action: 'move',
        left,
        top
      }))
    }
  }).on('mouse:up', () => {
    if (state.listening) {
      state.listening = false
      boardProcedure.send(messages.tapCastedMessage({
        action: 'stop'
      }))
    }
  })
}

// PROCEDURE LISTENERS

function procedureListener(message) {
  const { type, data } = message
  switch (type) {
    case 'board:shape:created': {
      const { type } = data
      let object = {}
      if (type === 'path') {
        fabric.Path.fromObject(data, (path) => {
          object = path
        })
      } else {
        object = new fabric[capitalizeFirst(type)](type === 'i-text' ? '' : null)
        object.set(data)
      }
      canvas.add(object).renderAll()
      break
    }
    case 'board:shape:modified': {
      const { id } = data
      const object = canvas.getObjectById(id)
      object.set(data).setCoords()
      canvas.renderAll()
      break
    }
    case 'board:shape:removed': {
      const { id } = data
      const object = canvas.getObjectById(id)
      canvas.remove(object).renderAll()
      break
    }
    case 'board:dart:casted': {
      const { action } = data
      if (action === 'stop') {
        canvas.remove(dart.shape)
      } else {
        dart.shape.set(data)
        if (action === 'start') {
          canvas.add(dart.shape)
        }
      }
      canvas.renderAll()
      break
    }
  }
}

function snapshotListener({ image, isThumbnail }) {
  return addSnapshot(image, isThumbnail)
}

// METHODS
function addSnapshot(data, isThumbnail) {
  return new Promise((resolve, reject) => {
    new fabric.Image().setSrc(data, (image) => {
      if (app.state.matches('app.view.meeting.draw')) {
        image.initSnapshot(user.profile.viewport)
        image.isThumbnail = isThumbnail
        const { scaleX: scale, left, top, width, height } = image
        snapshot.setTransform({ scale, left, top, width, height })
        const existingSnapshot = canvas.getObjects().find(obj => {
          return obj.type === 'image' && obj.isThumbnail
        })
        if (existingSnapshot) {
          canvas.remove(existingSnapshot)
        }
        canvas.add(image).sendToBack(image).renderAll()
        resolve()
      } else {
        reject()
      }
    })
  })
}
// LIFECYCLE HOOKS

onMounted(() => {
  const { width, height } = user.profile.viewport
  canvas = new fabric.Canvas('board', {
    width,
    height
  })
  canvas.renderAll()
  setCanvasEventHandlers()
})

messenger.subscribe('boardComponent', 'board:signal', (type) => {
  if (type === 'snapshot:save') {
    saveSnapshot()
  } else {
    console.warn('Logic has not been handled yet.')
  }
})

function saveSnapshot() {
  const { width: snapshotWidth, height: snapshotHeight } = snapshot.transform
  const { top, left, scale, client: { width: clientWidth, height: clientHeight } } = session.transform
  const { width: userWidth, height: userHeight } = user.profile.viewport
  const width = clientWidth * scale
  const height = clientHeight * scale
  const multiplier = 1 / Math.min(userWidth / snapshotWidth, userHeight / snapshotHeight, 1)
  const data = canvas.toDataURL({
    format: 'jpeg',
    quality: 1,
    left,
    top,
    width,
    height,
    multiplier
  })
  snapshotProcedure.save(data)
}
</script>
